import type { PermissionKey } from "@how/types";

/**
 * Bucket ids. how-public serves objects by URL without RLS; how-private always needs a signed URL; how-audio serves
 * sermon audio by URL, audio types only, 50 MB a file (ADR 0061).
 */
export const STORAGE_BUCKETS = {
  public: "how-public",
  private: "how-private",
  audio: "how-audio",
} as const;
export type StorageBucket = (typeof STORAGE_BUCKETS)[keyof typeof STORAGE_BUCKETS];

export interface StorageArea {
  readonly bucket: StorageBucket;
  readonly area: string;
  /** Permission required to list or download through the API (null: never through the API). */
  readonly read: PermissionKey | null;
  /** Permission required to upload, replace, or delete (null: server-side code only). */
  readonly write: PermissionKey | null;
  readonly description: string;
}

/**
 * Mirror of private.storage_area_permission(), newest in supabase/migrations/20261011130000_sermons_and_media.sql.
 * The SQL function is authoritative; this table exists so clients build paths they are allowed to use.
 */
export const STORAGE_AREAS: readonly StorageArea[] = [
  {
    bucket: "how-public",
    area: "branding",
    read: "settings.manage",
    write: "settings.manage",
    description: "Logos, favicons, brand images served publicly",
  },
  {
    bucket: "how-public",
    area: "media",
    read: "media.manage",
    write: "media.manage",
    description: "Public website media",
  },
  {
    bucket: "how-private",
    area: "people",
    read: "people.read",
    write: "people.write",
    description: "Person photos and documents",
  },
  {
    bucket: "how-private",
    area: "exports",
    read: "people.export",
    write: null,
    description: "Generated exports; written by jobs only",
  },
  {
    bucket: "how-private",
    area: "tenant",
    read: "settings.manage",
    write: "settings.manage",
    description: "Tenant-private documents",
  },
  {
    bucket: "how-audio",
    area: "sermons",
    read: "sermons.manage",
    write: "sermons.manage",
    description: "Sermon audio, served publicly for the website and the podcast (ADR 0061)",
  },
];

export function getStorageArea(bucket: StorageBucket, area: string): StorageArea | undefined {
  return STORAGE_AREAS.find((a) => a.bucket === bucket && a.area === area);
}
